import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

/**
 * ScoreBreakdownPopover — tiny (i) icon → on hover (desktop) or tap (mobile)
 * opens a transparent breakdown of how a pillar score is computed.
 *
 * Renders the popover into `document.body` via a portal so it escapes the
 * `.hpme-card` overflow-hidden context (otherwise the popover gets clipped
 * to the card's bounds — this was the visible bug on the dashboard).
 *
 * Position is computed from the trigger's getBoundingClientRect on open and
 * pinned to the viewport (position: fixed). It auto-flips horizontally so
 * it never overflows the viewport edge.
 *
 * Props:
 *   - breakdown:  { components: [{name, value, weight, contribution}], total_weight_present }
 *   - label:      pillar name shown in popover header
 *   - finalScore: the actual pillar score (null if no data)
 *   - color:      accent color for the popover header
 *   - testId
 */

const POPOVER_WIDTH = 288; // matches w-72

const ScoreBreakdownPopover = ({ breakdown, label, finalScore, color = '#CCFF00', testId }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  // Recompute position every time we open + on scroll/resize while open.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const place = () => {
      const r = triggerRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      // Default to centering under the trigger; clamp inside the viewport.
      let left = r.left + r.width / 2 - POPOVER_WIDTH / 2;
      left = Math.max(8, Math.min(left, vw - POPOVER_WIDTH - 8));
      setCoords({ top: r.bottom + 8, left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // Click outside / Escape closes.
  useEffect(() => {
    if (!open) return;
    const onClickOut = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClickOut);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOut);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!breakdown?.components) return null;
  const present = breakdown.components.filter(c => c.value != null);
  const missing = breakdown.components.filter(c => c.value == null);

  const popover = open ? createPortal(
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        width: POPOVER_WIDTH,
        zIndex: 1000,
      }}
      className="rounded-sm border border-zinc-700 bg-zinc-950/95 backdrop-blur-sm shadow-xl p-3"
      data-testid={`${testId}-popover`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[9px] uppercase tracking-[0.2em] font-secondary font-bold" style={{ color }}>{label}</p>
        {finalScore != null && (
          <p className="font-secondary font-bold text-lg leading-none" style={{ color }}>{finalScore}</p>
        )}
      </div>
      {present.length > 0 ? (
        <div className="space-y-1.5">
          {present.map((c, i) => (
            <div key={i} className="flex items-center justify-between text-[11px]" data-testid={`${testId}-row-${i}`}>
              <span className="text-zinc-400 flex-1 min-w-0 truncate pr-2">{c.name}</span>
              <span className="text-zinc-500 tabular-nums w-12 text-right">{c.value}</span>
              <span className="text-zinc-600 tabular-nums w-12 text-right">× {c.weight}%</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-zinc-500">No inputs logged yet — this fills in as you check in.</p>
      )}
      {missing.length > 0 && (
        <p className="mt-2 text-[10px] text-zinc-500 leading-relaxed border-t border-zinc-800 pt-2" data-testid={`${testId}-missing`}>
          Not measured yet: <span className="text-zinc-400">{missing.map(c => c.name).join(', ')}</span>.
        </p>
      )}
      {breakdown.total_weight_present != null && breakdown.total_weight_present > 0 && breakdown.total_weight_present < 100 && (
        <p className="mt-2 text-[10px] text-zinc-500 leading-relaxed">
          Score is renormalised over the <span className="text-zinc-300">{breakdown.total_weight_present}%</span> of inputs that have data.
        </p>
      )}
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="text-zinc-500 hover:text-zinc-300 transition-colors inline-flex"
        aria-label={`How ${label} is calculated`}
        data-testid={`${testId}-trigger`}
      >
        <Info className="w-3 h-3" />
      </button>
      {popover}
    </>
  );
};

export default ScoreBreakdownPopover;
